import React from "react";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import {
  useAuthentication,
  useEditAllocation,
  useEditTask,
} from "@planzone/view-models";
import { allocation } from "@planzone/dto";
import { Props } from "./types";
import styles from "./styles.scss";
import { TaskIcon } from "@common/taskIcon";
import classNames from "classnames";

function hashCode(str: string) {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return hash;
}

function intToRGB(i: number) {
  const c = (i & 0x00ffffff).toString(16).toUpperCase();

  return "00000".substring(0, 6 - c.length) + c;
}

function generateRandomColor(name: string) {
  return `#${intToRGB(hashCode(name))}`;
}

function isColorLight(color: string) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;

  return brightness > 155;
}

function Task({ task, isDiscontinuedLeft, isDiscontinuedRight }: Props) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { pushView } = useModal();
  const { editTask } = useEditTask(task);
  const { account } = useAuthentication();
  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });
  const backgroundColor = generateRandomColor(task.project.name);
  const isLight = isColorLight(backgroundColor);

  const isDone =
    task.percentComplete === 100 ||
    (task.status && task.status.percentComplete === 100);
  const userAllocation = task?.allocations?.find(
    (alloc) => alloc.resource.uuid === account.resource.uuid,
  );

  const project = task.project || task.project;

  return (
    <ScreenWatcher
      renderChildren={() => (
        <LayoutWatcher
          renderChildren={({ height: h, width: w }) => {
            const rect = ref.current && ref.current.getBoundingClientRect();
            const width = rect ? rect.width : w;
            const height = rect ? rect.height : h;

            return (
              <div
                ref={ref}
                className={styles.task}
                onClick={() =>
                  pushView({
                    data: { task, isMultiProject: true },
                    key: UseCases.retrieveTask,
                  })
                }
              >
                <svg
                  className={styles.background}
                  width={width + 2}
                  height={height + 2}
                  style={{ fill: backgroundColor, opacity: 0.6 }}
                >
                  <defs>
                    <filter id="shadow-task">
                      <feDropShadow
                        dx="0"
                        dy="0"
                        stdDeviation=".5"
                        floodColor="rgba(64, 87, 109, 0.15)"
                      />
                      <feDropShadow
                        dx=".5"
                        dy=".5"
                        stdDeviation="1"
                        floodColor="rgba(47, 64, 80, 0.25)"
                      />
                    </filter>
                  </defs>
                  <path
                    filter="url(#shadow-task)"
                    d={`
                ${
                  isDiscontinuedLeft
                    ? `
                M0 ${height / 2}
                L10 0
                `
                    : `
                M0 5
                C0 0, 0 0, 5 0
                `
                }
                ${
                  isDiscontinuedRight
                    ? `
                H${width - 10}
                L${width} ${height / 2}            
                `
                    : `
                H${width - 5}
                C${width} 0, ${width} 0, ${width} 5
                `
                }
                ${
                  isDiscontinuedRight
                    ? `
                L${width - 10} ${height}
                `
                    : `
                V${height - 15}
                C${width} ${height}, ${width} ${height}, ${width - 5} ${height}
                L${width - 5} ${height}
                `
                }
                ${
                  isDiscontinuedLeft
                    ? `
                H${10}
                L0 ${height / 2}
                `
                    : `
                H${5}
                C0 ${height}, 0 ${height}, 0 ${height - 15}
                L0 ${height}`
                }
                Z
            `}
                  />
                </svg>
                <div className={styles.main}>
                  <div
                    className={styles.taskCompletionStatus}
                    onClick={
                      project && !project.isTemplate
                        ? (e) => {
                            e.stopPropagation();
                            userAllocation && !isDone
                              ? task.allocations.length === 1
                                ? pushView({
                                    key: UseCases.markTaskAsCompleted,
                                    data: {
                                      task,
                                      project: project,
                                    },
                                  })
                                : editAllocation({
                                    allocation: userAllocation,
                                    nextAllocation: {
                                      ...userAllocation,
                                      percentComplete:
                                        userAllocation.percentComplete !== 100
                                          ? 100
                                          : 0,
                                    },
                                  })
                              : !isDone
                                ? pushView({
                                    key: UseCases.markTaskAsCompleted,
                                    data: {
                                      task,
                                      project: project,
                                    },
                                  })
                                : editTask({
                                    task,
                                    nextTask: { ...task, percentComplete: 0 },
                                  });
                          }
                        : undefined
                    }
                  >
                    <TaskCompletionStatus
                      progress={task.percentComplete}
                      endDate={task.endDate}
                      task={task}
                      resource={account.resource}
                    />
                  </div>
                  {task.percentComplete !== undefined && (
                    <p
                      className={styles.progress}
                      style={{
                        color: isLight ? "#909090" : "#e7e7e7",
                      }}
                    >
                      {Math.round(task.percentComplete)}%
                    </p>
                  )}
                  <p
                    className={styles.name}
                    style={{
                      color: isLight ? "#000000" : "#FFFFFF",
                    }}
                  >
                    <TaskIcon
                      className={classNames(styles.icon, styles.taskIcon)}
                    />
                    {task.name}
                  </p>
                </div>
              </div>
            );
          }}
        />
      )}
    />
  );
}

export { Task };
